/*
 * Copyright © 2024. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect, useState } from "react";

import { Helmet } from "react-helmet-async";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import { useInquiryToCustomers } from "@/src/hooks/queries/useInquiryToCustomers";
import PersonalAffection from "@/src/components/Form/ContractLaw/DataLeakContract/PersonalAffection";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import {
  fromBoolean,
  handleSimpleArray,
  reduceSimpleArray,
  toBoolean
} from "@/src/services/formServiceFunctions";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import _cloneDeep from "lodash/cloneDeep";
import { Navigate, useNavigate, useParams } from "react-router";
import PersonalAffectionSummary
  from "@/src/components/Form/ContractLaw/DataLeakContract/PersonalAffectionSummary";
import { InquiryToCustomerStatus } from "@/src/types/Entities/InquiryToCustomer";
import { getCaseLink } from "@/src/services/productService";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";

export default function DataLeakPersonalAffectionPage() {
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const [isApproval, setIsApproval] = useState(false);
  const [externalErrors] = useState({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const productApiUri = "/data_leak_contracts";
  const getProductId = () => {
    const { productId } = useParams();

    return productId ? parseInt(productId) : 0;
  };
  const productId = getProductId();
  const { data: product, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data),
  });

  const inquiryFilters = {
    identifier: "dataLeakPersonalInvolvementInquiry",
    productId: productId,
    "status[0]": InquiryToCustomerStatus.STATUS_OPEN,
    "status[1]": InquiryToCustomerStatus.STATUS_OVERDUE,
    "status[2]": InquiryToCustomerStatus.STATUS_IN_PROGRESS,
  };
  const { data: inquiries } = useInquiryToCustomers(inquiryFilters, !!product);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isApproval]);

  if (isLoading || !product || !inquiries) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  const currentInquiryToCustomer = inquiries[0];

  if (!currentInquiryToCustomer || currentInquiryToCustomer.identifier !== "dataLeakPersonalInvolvementInquiry") {
    return <Navigate to={getCaseLink(product)} />;
  }

  let personalAffection;

  if (product.personalAffection !== null) {
    personalAffection = {
      ...product.personalAffection,
      hadProblemsDueToCreditRating: fromBoolean(product.personalAffection.hadProblemsDueToCreditRating),
      emotionalState: reduceSimpleArray(product.personalAffection.emotionalState),
      purchasesPlannedInTheNearFuture: reduceSimpleArray(product.personalAffection.purchasesPlannedInTheNearFuture),
    };
  } else {
    personalAffection = {
      hadProblemsDueToCreditRating: {},
      creditRatingProblemsDescription: "",
      emotionalState: {},
      purchasesPlannedInTheNearFuture: {},
      purchasesPlannedInTheNearFutureOther: "",
      worriesFrequency: "",
      worriesFrequencyOther: "",
      personalStatement: "",
    };
  }

  const initialValues = {
    ...product,
    personalAffection: personalAffection,
  };

  const getMutationData = (values: AbstractCase) => {
    const updateData = _cloneDeep(values);

    if (!isApproval) {
      return {
        ...updateData.personalAffection,
        hadProblemsDueToCreditRating: toBoolean(updateData.personalAffection.hadProblemsDueToCreditRating),
        emotionalState: handleSimpleArray(updateData.personalAffection.emotionalState),
        purchasesPlannedInTheNearFuture: handleSimpleArray(updateData.personalAffection.purchasesPlannedInTheNearFuture),
      };
    } else {
      return {
        id: updateData.personalAffection.id,
        personalAffectionClient: updateData.personalAffection.personalAffectionClient,
        clearInquiry: true,
      };
    }
  };

  const handleSubmit = async ({ values }: { values: AbstractCase }) => {
    setIsLoadingInternal(true);

    const mutationData = getMutationData(values);

    await updateMutation.mutateAsync({
      uri: "/data_leak_personal_affections",
      id: mutationData.id,
      data: mutationData,
    });

    await queryClient.invalidateQueries({
      queryKey: queryKeys.item("/data_leak_contracts", product.id),
    });

    if (isApproval) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.collections("/inquiry_to_customers") });
    }

    setIsLoadingInternal(false);

    if (!isApproval) {
      setIsApproval(true);
    } else {
      navigate("/servicewelt");
    }
  };

  return (
    <LayoutProtectedRoutes>
      <Helmet>
        <title>Datenweitergabe - Persönliche Betroffenheit | Legalbird</title>
      </Helmet>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit} externalErrors={externalErrors}>
        {!isApproval && <PersonalAffection submitProps={{ isLoading: isLoadingInternal, isMinimal: true, hideBackButton: true }} />}
        {isApproval && (
          <PersonalAffectionSummary
            product={product}
            submitProps={{
              nextText: "Rückmeldung übermitteln",
              isLoading: isLoadingInternal,
              isMinimal: true,
              onBackClick: (e) => {
                e.preventDefault();
                setIsApproval(false);
                return false;
              },
            }}
          />
        )}
      </FormProvider>
    </LayoutProtectedRoutes>
  );
}
